/**
 * Tank Mix Calculator engine — pure functions, no side effects.
 *
 * Converts a (product, tank size, application mode) selection into a complete
 * mixing recipe: product amount in fl oz AND teaspoons (homeowners measure in
 * teaspoons), surfactant amount, and the four-step mixing order.
 */

import {
  TANK_MIX_PRODUCTS,
  TANK_MIX_SURFACTANTS,
  type TankMixProduct,
} from '@/data/tank-mix-products'

export type ApplicationMode = 'spot' | 'blanket'

export interface TankMixInput {
  productId: string
  tankGallons: number
  mode: ApplicationMode
}

export interface TankMixResult {
  product: TankMixProduct
  tankGallons: number
  mode: ApplicationMode
  /** Total product for the tank */
  productTsp: number
  productOz: number
  /** Display-friendly rounded strings */
  productTspLabel: string
  productOzLabel: string
  /** Surfactant line (null when the formulation includes one) */
  surfactant: {
    name: string
    tsp: number
    oz: number
  } | null
  /** Ordered mixing steps, ready to render */
  steps: string[]
  caution: string
  /** e.g. "Tenacity · 2 gal · spot" */
  recipeTitle: string
}

const TSP_PER_OZ = 6

function formatOz(oz: number): string {
  return oz < 1 ? oz.toFixed(2) : oz.toFixed(1)
}

function formatTsp(tsp: number): string {
  return Number.isInteger(tsp) ? String(tsp) : tsp.toFixed(1)
}

function formatGallons(gal: number): string {
  return Number.isInteger(gal) ? String(gal) : String(gal)
}

export function calculateTankMix(input: TankMixInput): TankMixResult {
  const product = TANK_MIX_PRODUCTS[input.productId]
  if (!product) {
    throw new Error(`Unknown tank mix product: ${input.productId}`)
  }
  if (!Number.isFinite(input.tankGallons) || input.tankGallons <= 0) {
    throw new Error(`Invalid tank size: ${input.tankGallons}`)
  }

  const tspPerGal = input.mode === 'spot' ? product.spotTspPerGal : product.blanketTspPerGal
  const productTsp = tspPerGal * input.tankGallons
  const productOz = productTsp / TSP_PER_OZ

  let surfactant: TankMixResult['surfactant'] = null
  let surfactantStep: string
  if (product.surfactant === 'none') {
    surfactantStep = 'No surfactant needed — this formulation already includes one'
  } else {
    const info = TANK_MIX_SURFACTANTS[product.surfactant]
    const surfTsp = info.tspPerGal * input.tankGallons
    const surfOz = surfTsp / TSP_PER_OZ
    surfactant = { name: info.name, tsp: surfTsp, oz: surfOz }
    const stepName = info.name.charAt(0).toLowerCase() + info.name.slice(1)
    surfactantStep = `Add ${stepName}, ${formatTsp(surfTsp)} tsp (${formatOz(surfOz)} fl oz)`
  }

  const halfWater = input.tankGallons / 2
  const productTspLabel = formatTsp(productTsp)
  const productOzLabel = formatOz(productOz)

  const steps = [
    `Fill the tank halfway with ${formatGallons(halfWater)} gal of water`,
    `Add ${productOzLabel} fl oz (${productTspLabel} tsp) of ${product.name} and agitate`,
    surfactantStep,
    `Top off to ${formatGallons(input.tankGallons)} gal, agitate again, and spray`,
  ]

  return {
    product,
    tankGallons: input.tankGallons,
    mode: input.mode,
    productTsp,
    productOz,
    productTspLabel,
    productOzLabel,
    surfactant,
    steps,
    caution: product.caution,
    recipeTitle: `${product.name} · ${input.tankGallons} gal · ${input.mode === 'spot' ? 'spot' : 'blanket'}`,
  }
}
